import {
  FunctionComponent,
  useCallback,
  useState,
  useRef,
  ChangeEvent,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Grid } from '@material-ui/core'
import { FormikValues, FormikProps } from 'formik'
import * as Yup from 'yup'

import { Feedback } from 'shared/components/Feedback'
import Form from 'shared/components/Form'
import Input from 'shared/components/Input'
import Modal from 'shared/components/Modal'
import SnackBar from 'shared/components/SnackBar'
import { colors } from 'shared/styles/colors'
import { cepMask, phoneMask } from 'shared/utils/formatters'
import iconConcluido from 'shared/assets/icons/icon-concluido-blue.png'
import ContainerPage from 'shared/components/ContainerPage'
import SizedBox from 'shared/components/SizedBox'
import dimensions from 'shared/styles/dimensions'
import IMotorista from 'modules/motorista/domain/entities/IMotorista'
import { IConsultZipCode } from 'modules/user/domain/usecases/ConsultZipCodeImpl'

import { IUpdateMotorista } from '../../domain/usecases/UpdateMotoristaImpl'
import { Field, Title, Value } from './styles'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'

type TProp = {
  updateMotorista: IUpdateMotorista
  consultZipCode: IConsultZipCode
}

type TLocation = {
  params: IMotorista
}

const UpdateMotorista: FunctionComponent<TProp> = ({
  updateMotorista,
  consultZipCode,
}) => {
  const history = useHistory()
  const location = useLocation<TLocation>()
  const [motorista, setMotorista] = useState(location.state?.params)
  const [errorMessage, setErrorMessage] = useState(null)
  const [error, setError] = useState(false)
  const formRef = useRef<FormikProps<FormikValues>>(null)
  const [formOk, setFormOk] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [errorMessageSnackBar, setErrorMessageSnackBar] = useState('')
  const [showingError, setShowingError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleCepChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setErrorMessage('')
      setFormOk(false)
      if (!e.target.value) {
        setMotorista({ ...motorista, endereco: null })

        return
      }

      setLoading(true)

      try {
        const schema = Yup.object().shape({
          cep: Yup.string().matches(/^([\d]{5})-*([\d]{3})/),
        })

        await schema.validate(
          {
            cep: e.target.value,
          },
          {
            abortEarly: false,
          },
        )
        const response = await consultZipCode.execute({ cep: e.target.value })

        if (response.cidade) {
          setMotorista({
            ...motorista,
            endereco: {
              cep: e.target.value,
              ...response,
              complemento: '',
              numero: '',
            },
          })
          setFormOk(true)
        } else {
          setMotorista({
            ...motorista,
            endereco: {
              logradouro: null,
              numero: null,
              complemento: null,
              bairro: null,
              cidade: null,
              estado: null,
              cidade_ibge: null,
              cep: e.target.value,
            },
          })
          setShowingError(true)
          setErrorMessageSnackBar('Cep inválido.')
        }
      } catch (err: unknown) {
        if (err instanceof Yup.ValidationError) {
          setFormOk(false)
          return
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro ao lidar com alteração do cep')
        }
        setError(true)
      } finally {
        setLoading(false)
      }
    },
    [motorista, consultZipCode],
  )

  const handleSubmit = async () => {
    setErrorMessage('')
    setLoading(true)

    try {
      const dadosUpdate = {
        nome: motorista.nome,
        telefone: motorista.telefone,
        email: motorista.email,
        endereco: {
          cep: motorista.endereco.cep,
          numero: motorista.endereco.numero,
          complemento: motorista.endereco.complemento,
          logradouro: motorista.endereco.logradouro,
          bairro: motorista.endereco.bairro,
          cidade: motorista.endereco.cidade,
          estado: motorista.endereco.estado,
          cidade_ibge: motorista.endereco.cidade_ibge,
        },
      }
      await updateMotorista.execute(dadosUpdate, motorista.id)
      setOpenModal(true)
    } catch (err: unknown) {
      if (err instanceof Yup.ValidationError) {
        setErrorMessage(err.message)
        return
      } else if (err instanceof Error) {
        setErrorMessage(err.message)
      } else {
        setErrorMessage('Erro ao tentar atualizar o motorista')
      }
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const handleNomeChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setErrorMessage('')
      setFormOk(false)

      try {
        const schema = Yup.object().shape({
          nome: Yup.string().required('Nome inválido!'),
        })

        await schema.validate(
          {
            nome: e.target.value,
          },
          {
            abortEarly: false,
          },
        )

        setMotorista({ ...motorista, nome: e.target.value })
        setFormOk(true)
      } catch (err: unknown) {
        if (err instanceof Yup.ValidationError) {
          setFormOk(false)
          setErrorMessage(err.message)
          return
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro ao lidar com alteração do nome')
        }
        setError(true)
      }
    },
    [],
  )
  const salvarMotorista = () => {
    formRef.current.submitForm()
  }

  const handlePhoneChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setErrorMessage('')
      setFormOk(false)

      if (!e.target.value) {
        setMotorista({ ...motorista, telefone: '' })

        return
      }

      try {
        const schema = Yup.object().shape({
          phone: Yup.string()
            .required()
            .matches(/\(\d{2,}\) \d{4,}-\d{4}/g),
        })

        await schema.validate(
          {
            phone: e.target.value,
          },
          {
            abortEarly: false,
          },
        )

        setMotorista({ ...motorista, telefone: e.target.value })
        setFormOk(true)
      } catch (err: unknown) {
        if (err instanceof Yup.ValidationError) {
          setFormOk(false)
          return
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro ao lidar com alteração do telefone')
        }
        setError(true)
      }
    },
    [motorista],
  )

  const handleEmailChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      setErrorMessage('')
      setFormOk(false)

      try {
        const schema = Yup.object().shape({
          email: Yup.string()
            .notRequired()
            .email('Email inválido')
            .nullable(true),
        })

        await schema.validate(
          {
            email: e.target.value,
          },
          {
            abortEarly: false,
          },
        )

        setMotorista({ ...motorista, email: e.target.value })
        setFormOk(true)
      } catch (err: unknown) {
        if (err instanceof Yup.ValidationError) {
          setFormOk(false)
          return
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro ao lidar com alteração do email')
        }
        setError(true)
      }
    },
    [motorista],
  )

  if (!location?.state?.params) {
    history.push('/motoristas')
    return <></>
  }

  return (
    <>
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <ContainerPage
        breadCrumb="Motoristas / Lista de motoristas / Editar motorista"
        showFooterScaffold={true}
        onClickBackButton={() => {
          history.push('/motoristas')
        }}
        titleBackButton="Voltar"
        onClickNextButton={salvarMotorista}
        titleNextButton="Salvar"
        colorNextButton={`${formOk ? colors.green : colors.gray}`}
        isNextButtonDisabled={!formOk}
        title="Editar motoristas"
      >
        <Form
          enableReinitialize
          innerRef={formRef}
          onSubmit={handleSubmit}
          initialValues={{
            nome: motorista.nome || '',
            telefone: motorista.telefone || '',
            email: motorista.email || '',
            cep: motorista.endereco?.cep || '',
            address: motorista.endereco?.logradouro || '',
            number: motorista.endereco?.numero || '',
            neighborhood: motorista.endereco?.bairro || '',
            city: motorista.endereco?.cidade || '',
            state: motorista.endereco?.estado || '',
            complement: motorista.endereco?.complemento || '',
          }}
          validationSchema={() => ({
            nome: Yup.string().required('Nome inválido!'),
            telefone: Yup.string().required('Telefone inválido!'),
            cep: Yup.string().required('CEP inválido'),
            email: Yup.string()
              .notRequired()
              .email('Email inválido')
              .nullable(true),
            address: Yup.string().required('Logradouro inválido!'),
            number: Yup.string().required('Número inválido!'),
            neighborhood: Yup.string().required('Bairro inválido!'),
            city: Yup.string().required('Cidade inválido!'),
            state: Yup.string().required('Estado inválido!'),
            complement: Yup.string().optional(),
          })}
        >
          {formControl => (
            <Grid container>
              <Grid container spacing={1}>
                <Grid item xs={12} md={4}>
                  <Input
                    label="Nome do motorista"
                    placeholder="Nome do motorista"
                    name="nome"
                    formControl={formControl}
                    onChange={handleNomeChange}
                    errorMessage={errorMessage}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field>Nº da CNH</Field>
                  <Value>{motorista.cnh}</Value>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field>Vencimento da CNH</Field>
                  <Value>{motorista.vencimento_cnh}</Value>
                </Grid>
              </Grid>
              <Grid container style={{ marginTop: '10px' }}>
                <Grid item xs={12} md={4}>
                  <Field>Categoria</Field>
                  <Value>{motorista.categoria_cnh}</Value>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field>CPF</Field>
                  <Value>{motorista.cpf}</Value>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Field>RG</Field>
                  <Value>{motorista.rg}</Value>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={12} md={4}>
                  <Field>Local de nascimento</Field>
                  <Value>{`${motorista.local_emissao_cnh} - ${motorista.estado_emissao_cnh}`}</Value>
                </Grid>
              </Grid>

              <Grid container>
                <Grid container item xs={12} spacing={4}>
                  <Grid item xs={12}>
                    <Title>Telefone</Title>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      label="Telefone"
                      placeholder="DDD + Número"
                      name="telefone"
                      formControl={formControl}
                      mask={value => phoneMask(value)}
                      onChange={handlePhoneChange}
                      maxLength={15}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Title>Email</Title>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      label="Email"
                      placeholder="Seu email"
                      name="email"
                      formControl={formControl}
                      onChange={handleEmailChange}
                    />
                  </Grid>
                  <Grid item xs={12} spacing={4}>
                    <SizedBox size={dimensions.spaces.large} />
                    <Title>Endereço</Title>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      label="CEP"
                      placeholder="Digite o CEP"
                      name="cep"
                      type="text"
                      formControl={formControl}
                      mask={value => cepMask(value)}
                      onChange={handleCepChange}
                      maxLength={9}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      label="Logradouro"
                      placeholder="Informe a rua"
                      name="address"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotorista({
                          ...motorista,
                          endereco: {
                            ...motorista.endereco,
                            logradouro: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Input
                      label="Número"
                      placeholder="Informe o número"
                      name="number"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotorista({
                          ...motorista,
                          endereco: {
                            ...motorista.endereco,
                            numero: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      label="Complemento"
                      placeholder="Opcional"
                      name="complement"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotorista({
                          ...motorista,
                          endereco: {
                            ...motorista.endereco,
                            complemento: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Input
                      label="Bairro"
                      placeholder="Informe o bairro"
                      name="neighborhood"
                      formControl={formControl}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setMotorista({
                          ...motorista,
                          endereco: {
                            ...motorista.endereco,
                            bairro: e.target.value,
                          },
                        })
                      }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Input
                      label="Cidade"
                      placeholder="Informe a cidade"
                      name="city"
                      formControl={formControl}
                      disabled={true}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Input
                      label="Estado"
                      placeholder="Informe o estado"
                      name="state"
                      formControl={formControl}
                      disabled={true}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Form>
        <SnackBar
          open={error}
          setOpen={setError}
          severity="error"
          message={errorMessage}
          horizontal="right"
        />
        <Modal size="small" openModal={openModal} showCloseButton={false}>
          <Feedback
            color={colors.blue}
            icon={iconConcluido}
            showLogo={false}
            title="Atualização realizada com sucesso!"
            sizeIcon={100}
            onClickButton={() => {
              history.push('/motoristas')
            }}
            backgroundColor={colors.white}
          />
        </Modal>
        <SnackBar
          open={showingError}
          setOpen={setShowingError}
          severity="error"
          message={errorMessageSnackBar}
          horizontal="right"
        />
      </ContainerPage>
    </>
  )
}

export default UpdateMotorista
